import * as indoorclimate from './api-client/models/indoorclimate/Contracts'
import * as portfolio from './api-client/models/portfolio/Contracts'
import * as project from './api-client/models/project/Contracts'
import * as energy from './api-client/models/energy/Contracts'
import * as tickets from './api-client/models/tickets/Contracts'
import { LocaleString } from './api-client/models/utilization/Contracts'
import { Coloring } from './components/AnalyticsDashboard/components/MeasurementCard.vue'
import { Cell, Table } from './components/AnalyticsDashboard/components/TableDataSource.vue'
import { FormatOptions } from './components/AnalyticsDashboard/formatter'
import { typeToCategory } from './api-client/models/energy/Contracts'
import { BarValue } from './components/AnalyticsDashboard/components/ChartComponent/chart/bar'

type Transform<From, To, Options> = (
  source: { from?: From; isLoading?: boolean },
  options: Options & { getLocaleMessage: (locale: string) => Record<string, string> }
) => To | undefined

type Transforms = {
  path: Transform<any, any, { path: string }>
  'portfolio.apartments.colored-entities': Transform<
    portfolio.MeasurementResponse,
    Coloring[],
    { index?: number }
  >
  'portfolio.apartments.histogram': Transform<
    portfolio.HistogramResponse,
    indoorclimate.HistogramValue[],
    { index?: number }
  >
  'projects.detail': Transform<
    project.MeasurementResponse,
    Table['rows'],
    { status: project.ProjectStatus; detailRowNames: { [key: string]: LocaleString } }
  >
  'tickets.detail': Transform<indoorclimate.KpiResponse, Table['rows'], {}>
  'tickets.timeseries': Transform<tickets.TimeSeriesResponse, indoorclimate.TimeSeriesValue[], {}>
  'climate.index.colored-entities': Transform<
    indoorclimate.KpiResponse,
    Coloring[],
    { index?: number }
  >
  'ticket.colored-entities': Transform<indoorclimate.KpiResponse, Coloring[], { index?: number }>
  'climate.index.detail': Transform<
    indoorclimate.KpiResponse,
    Table['rows'],
    { detailRowNames: { [key: string]: LocaleString }; format: FormatOptions }
  >
  'energy.actual.detail': Transform<
    energy.KpiResponse,
    Table['rows'],
    { detailRowNames: { [key: string]: LocaleString }; format: FormatOptions }
  >
  'energy.actual.colored-entities': Transform<energy.KpiResponse, Coloring[], { index?: number }>
  'energy.timeseries': Transform<energy.TimeSeriesResponse, BarValue[], { type: energy.EnergyType }>
}

export type TransformId = keyof Transforms

export type FromOfTransform<Id extends TransformId> = Parameters<Transforms[Id]>[0]['from']
export type ToOfTransform<Id extends TransformId> = ReturnType<Transforms[Id]>
export type OptionsOfTransform<Id extends TransformId> = Parameters<Transforms[Id]>[1]

export const transforms: Transforms = {
  path: ({ from: data }, { path }): string | undefined => {
    path = path.replace(/\[(\w+)\]/g, '.$1')
    path = path.replace(/^\./, '')
    const splitPath = path.split('.')
    for (let i = 0, n = splitPath.length; i < n; ++i) {
      const key = splitPath[i]
      if (data && key in data) {
        data = data[key]
      } else {
        return
      }
    }
    return data
  },
  'portfolio.apartments.colored-entities': ({ from }, options?: { index?: number }) => {
    const index = options?.index ?? 0
    const measurement = from?.measurements[index]

    return measurement?.splits
      ?.filter((split) => split.key && split.value !== null)
      .map((split) => ({
        id: split.key!,
        value: split.value!
      }))
  },
  'portfolio.apartments.histogram': ({ from }, { index }) => {
    const histogram = from?.histograms[index ?? 0]
    return histogram?.bins?.map((bin) => ({
      start: bin.x,
      end: bin.x + 1,
      value: bin.y
    }))
  },
  'projects.detail': ({ from }, { status, detailRowNames }): Table['rows'] | undefined => {
    const measurements = from?.measurements.filter((measurement) => measurement.status === status)
    return measurements?.flatMap(
      (measurement): Cell[][] =>
        measurement?.splits?.map((split): Cell[] => [
          {
            type: 'text',
            text: detailRowNames[measurement.type!]
          },
          {
            type: 'number',
            number: split.value ?? undefined,
            isLoading: false,
            format: {
              format: {
                type: 'intl-number',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }
            }
          }
        ]) ?? []
    )
  },
  'tickets.timeseries': ({ from }) => {
    return from?.values.map((value) => ({
      timestamp: value.date,
      value: value.numberOfTickets
    }))
  },
  'tickets.detail': ({ from }, { getLocaleMessage }): Table['rows'] | undefined => {
    return from?.kpis.flatMap(
      (measurement): Cell[][] =>
        measurement?.values
          ?.filter((s) => s.splitKey !== undefined)
          ?.map((split): Cell[] => [
            {
              type: 'text',
              text: {
                en: getLocaleMessage('en')[split.splitKey!],
                sv: getLocaleMessage('sv')[split.splitKey!]
              }
            },
            {
              type: 'number',
              number: split.value ?? undefined,
              isLoading: false,
              format: {
                format: {
                  type: 'intl-number',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }
              }
            }
          ]) ?? []
    )
  },
  'ticket.colored-entities': ({ from }, { index }) => {
    const kpis = from?.kpis
    const kpi = kpis?.[index ?? 0]
    return kpi?.values
      ?.map((value) => ({
        id: value.splitKey,
        meta: value.meta,
        value: value.value
      }))
      .filter((value) => value.id !== null) as Coloring[]
  },
  'climate.index.colored-entities': ({ from }, { index }) => {
    const kpis = from?.kpis
    const kpi = kpis?.[index ?? 0]
    return kpi?.values
      ?.map((value) => ({
        id: value.splitKey,
        value: value.value
      }))
      .filter((value) => value.id !== null) as Coloring[]
  },
  'climate.index.detail': ({ from }, { detailRowNames, format }) => {
    const kpis = from?.kpis
    return kpis?.flatMap((kpi): Cell[][] =>
      kpi.values.map((value) => [
        {
          type: 'text',
          text: detailRowNames[kpi.type]
        },
        {
          type: 'number',
          number: value.value,
          isLoading: false,
          format
        }
      ])
    )
  },
  'energy.actual.detail': ({ from }, { detailRowNames, format }) => {
    const kpis = from?.kpis
    return kpis?.flatMap((kpi): Cell[][] =>
      kpi.values.map((value) => [
        {
          type: 'text',
          text: detailRowNames[typeToCategory(kpi.type)]
        },
        {
          type: 'number',
          number: value.value,
          isLoading: false,
          format
        }
      ])
    )
  },
  'energy.actual.colored-entities': ({ from }, { index }) => {
    return from?.kpis[index ?? 0].values
      .filter((x) => x.splitKey !== undefined)
      .map(
        (value): Coloring => ({
          id: value.splitKey!,
          value: value.value
        })
      )
  },
  'energy.timeseries': ({ from }, { type }): BarValue[] | undefined => {
    return from
      ? from.points.map((point) => ({
          label: point.timestamp,
          height: point.values[type] ?? 0
        }))
      : undefined
  }
}
